import {
  beginMailMutation,
  cancelMailMutation,
  failMailMutation,
  finishMailMutation,
  setMailMutationLabel,
  setMailMutationPhase,
  type MailActivityKind,
} from "@/lib/mail/mailMutationProgress";
import { extractSenderLabel } from "@/lib/formatMailDate";

function trimText(raw: string, max: number): string {
  const t = raw.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1))}…`;
}

function trimSubject(subject: string | null | undefined, fallback: string): string {
  return trimText(subject?.trim() || fallback, 64);
}

/** Ligne journal : « Expéditeur — Objet » (jamais Message #uid). */
export function mailActivityMessageStepLabel(
  msg: { from?: string; subject?: string | null } | null | undefined
): string {
  const subject = trimText(msg?.subject?.trim() || "Sans objet", 48);
  const fromRaw = msg?.from?.trim();
  if (!fromRaw) return subject;
  const from = trimText(extractSenderLabel(fromRaw), 28);
  return `${from} — ${subject}`;
}

/** Démarre une entrée journal (observation — ne change pas le métier). */
export function startMailActivity(
  label: string,
  kind: MailActivityKind,
  phase: "local" | "imap-sync" = "imap-sync"
): string {
  const id = beginMailMutation(label, { kind, keepHistory: true });
  setMailMutationPhase(id, phase);
  return id;
}

export function completeMailActivity(id: string | null | undefined, label?: string): void {
  if (!id) return;
  if (label?.trim()) setMailMutationLabel(id, label.trim());
  finishMailMutation(id);
}

/** Retire une entrée journal sans la garder (spam / annulation sans travail). */
export function discardMailActivity(id: string | null | undefined): void {
  if (!id) return;
  cancelMailMutation(id);
}

export function failMailActivity(id: string | null | undefined, error: string): void {
  if (!id) return;
  failMailMutation(id, error.trim() || "Échec");
}

export function mailActivitySubjectLabel(
  prefix: string,
  subject: string | null | undefined
): string {
  return `${prefix} « ${trimSubject(subject, "Sans objet")} »`;
}

function mailboxAddress(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const angle = /<([^>]+)>/.exec(trimmed);
  return (angle?.[1] ?? trimmed).trim();
}

/** Destinataires lisibles pour le journal (À + Cc). */
export function formatMailActivityRecipients(
  to?: string | null,
  cc?: string | null
): string {
  const addrs = [to, cc]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(",")
    .split(",")
    .map(mailboxAddress)
    .filter(Boolean);
  if (addrs.length === 0) return "";
  if (addrs.length <= 2) return addrs.join(", ");
  const extra = addrs.length - 2;
  return `${addrs[0]}, ${addrs[1]} et ${extra} autre${extra > 1 ? "s" : ""}`;
}

/** Ligne d’envoi : objet + de qui + à qui. */
export function mailActivitySendLabel(options: {
  prefix: string;
  subject?: string | null;
  from?: string | null;
  to?: string | null;
  cc?: string | null;
}): string {
  const head = mailActivitySubjectLabel(options.prefix, options.subject);
  const from = mailboxAddress(options.from ?? "");
  const to = formatMailActivityRecipients(options.to, options.cc);
  if (from && to) return trimText(`${head} — de ${from} à ${to}`, 480);
  if (from) return trimText(`${head} — de ${from}`, 480);
  if (to) return trimText(`${head} — à ${to}`, 480);
  return head;
}

export function splitMailActivityLabel(label: string): { title: string; detail?: string } {
  const marker = " — ";
  const idx = label.indexOf(marker);
  if (idx < 0) return { title: label };
  return { title: label.slice(0, idx), detail: label.slice(idx + marker.length) };
}
